// Extra CMS slots for the 'what_we_are_and_arent' page — added to make previously-hardcoded
// public marketing copy editable. Concatenated into CMS_SLOTS via ./index.
// Each default MUST equal the exact copy the component rendered before wiring,
// so the public page is byte-identical until an admin edits it.
import type { SlotDef } from '../slots';

export const whatWeAreExtra: SlotDef[] = [
  // ─── Definition — dictionary headword (was hardcoded across three spans) ─
  // Kept as three slots so each span keeps its own styling (the headword uses
  // the Bricolage display face; pronunciation + part-of-speech are italic).
  {
    key: 'what_we_are_and_arent.definition_headword',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Definition: headword',
    helpText: 'The large dictionary headword (rendered in the display face).',
    default: 'wwaave',
  },
  {
    key: 'what_we_are_and_arent.definition_pronunciation',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Definition: pronunciation',
    helpText: 'The italic phonetic note beside the headword.',
    default: '/wāv/',
  },
  {
    key: 'what_we_are_and_arent.definition_part_of_speech',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Definition: part of speech',
    helpText: 'The italic dictionary abbreviation (e.g. "n.").',
    default: 'n.',
  },

  // ─── The model — inline CTA link labels (destinations stay fixed in code) ─
  {
    key: 'what_we_are_and_arent.model_cta_founding',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'The model: founding-offer link label',
    helpText: 'Link text only; the destination (Founding venue offer) is fixed.',
    default: 'Founding venue offer →',
  },
  {
    key: 'what_we_are_and_arent.model_cta_faq',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'The model: FAQ link label',
    helpText: 'Link text only; the destination (the FAQ) is fixed.',
    default: 'Open the FAQ →',
  },

  // ─── Read on — closing next-steps block (block heading + per-tile copy) ──
  // Tile destinations stay hardcoded in JSX; each tile's title + description is
  // mapped to its own slot (never zipped by index).
  {
    key: 'what_we_are_and_arent.next_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Read on: title',
    default: 'Read on.',
  },
  {
    key: 'what_we_are_and_arent.next_lede',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Read on: lede',
    default: 'The promise is the principle. The next pages show how it plays out in the everyday.',
    // Removed from the page at Richie's request — the "Read on" subheading was
    // dropped, so this slot no longer renders. Retired (not deleted) so any
    // future override doesn't orphan and it leaves the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.next_founding_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Read on: founding-venues tile title',
    default: 'Founding venues',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.next_founding_desc',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Read on: founding-venues tile description',
    default: 'The offer to the first 50 venues, plain numbers.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.next_dispute_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Read on: dispute-resolution tile title',
    default: 'Dispute resolution',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.next_dispute_desc',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Read on: dispute-resolution tile description',
    default: 'How we handle exchanges that go wrong: categories, steps, principles.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.next_faq_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Read on: FAQ tile title',
    default: 'FAQ',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.next_faq_desc',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Read on: FAQ tile description',
    default: 'The questions we hear most: free?, verification, disputes, reviews.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },

  // ─── Closing CTA (under the founder's note) ────────────────────────────
  // Matches the /about pattern: artist/host sign-up pair. Destinations
  // (/waitlist or /sign-up, resolved by JoinLink) stay fixed in code; editing
  // here changes the visible button label only.
  {
    key: 'what_we_are_and_arent.cta_artist',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Closing CTA: artist button',
    helpText: 'Label on the artist sign-up button under the founder’s note.',
    default: "I'm an artist",
  },
  {
    key: 'what_we_are_and_arent.cta_host',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Closing CTA: host button',
    helpText: 'Label on the host sign-up button under the founder’s note.',
    default: 'I host a venue',
  },
];
